(function (root, factory) {
  var engine = factory();
  if (typeof module === 'object' && module.exports) {
    module.exports = engine;
  }
  root.MortgageEngine = engine;
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  function toNumber(value, fallback) {
    var parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  function calculateMonthlyPI(loanAmount, monthlyInterestRate, numberOfPayments) {
    if (loanAmount <= 0 || numberOfPayments <= 0) return 0;
    if (monthlyInterestRate <= 0) return loanAmount / numberOfPayments;
    var r = monthlyInterestRate;
    var n = numberOfPayments;
    var p = loanAmount;
    return p * (r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
  }

  function buildAmortizationSchedule(input, extraMonthly, startYear) {
    var schedule = [];
    var balance = Math.max(0, input.homePrice - input.downPayment);
    var monthlyInterestRate = input.interestRate / 100 / 12;
    var numberOfPayments = input.loanTermYears * 12;
    var monthlyPayment = calculateMonthlyPI(balance, monthlyInterestRate, numberOfPayments);

    var yearPrincipal = 0;
    var yearInterest = 0;
    var month = 0;

    while (balance > 0.01 && month < numberOfPayments + 120) {
      month++;

      var interestPayment = balance * monthlyInterestRate;
      var principalPayment = monthlyPayment - interestPayment + extraMonthly;

      if (principalPayment > balance) {
        principalPayment = balance;
      }

      balance -= principalPayment;
      yearPrincipal += principalPayment;
      yearInterest += interestPayment;

      if (month % 12 === 0 || balance <= 0.01) {
        var year = startYear + Math.ceil(month / 12);
        var equity = input.homePrice > 0 ? ((input.homePrice - balance) / input.homePrice) * 100 : 0;

        schedule.push({
          year: year,
          principalPaid: yearPrincipal,
          interestPaid: yearInterest,
          endingBalance: Math.max(0, balance),
          equityPercent: Math.min(100, equity).toFixed(1)
        });

        yearPrincipal = 0;
        yearInterest = 0;
      }

      if (balance <= 0.01) break;
    }

    return schedule;
  }

  function calculate(input, nowYear) {
    var startYear = nowYear || new Date().getFullYear();
    var loanAmount = Math.max(0, input.homePrice - input.downPayment);
    // The exact ratio decides PMI; the rounded figure is display-only. Rounding
    // before the comparison waived PMI for down payments of 19.5-19.99%.
    var exactDownPaymentRatio = input.homePrice > 0 ? (input.downPayment / input.homePrice) * 100 : 0;
    var downPaymentPercent = Math.round(exactDownPaymentRatio);
    var monthlyInterestRate = input.interestRate / 100 / 12;
    var numberOfPayments = input.loanTermYears * 12;
    var monthlyPI = calculateMonthlyPI(loanAmount, monthlyInterestRate, numberOfPayments);

    var monthlyTaxInsurance = (input.propertyTax / 12) + (input.homeInsurance / 12);
    if (exactDownPaymentRatio < 20) {
      monthlyTaxInsurance += input.pmi;
    }
    monthlyTaxInsurance += input.hoa;

    var amortizationSchedule = buildAmortizationSchedule(input, 0, startYear);
    var amortizationScheduleWithExtra = input.extraPayment > 0
      ? buildAmortizationSchedule(input, input.extraPayment, startYear)
      : [];

    var totalPaid = input.extraPayment > 0
      ? amortizationScheduleWithExtra.reduce(function (sum, year) { return sum + year.principalPaid + year.interestPaid; }, 0)
      : monthlyPI * numberOfPayments;

    var totalInterest = input.extraPayment > 0
      ? amortizationScheduleWithExtra.reduce(function (sum, year) { return sum + year.interestPaid; }, 0)
      : (monthlyPI * numberOfPayments) - loanAmount;

    var totalInterestWithoutExtra = (monthlyPI * numberOfPayments) - loanAmount;
    var actualLoanTerm = (input.extraPayment > 0 && amortizationScheduleWithExtra.length > 0)
      ? amortizationScheduleWithExtra.length
      : input.loanTermYears;

    var chartSchedule = input.extraPayment > 0 ? amortizationScheduleWithExtra : amortizationSchedule;

    var cumulativePrincipal = input.downPayment;
    var cumulativeInterest = 0;
    var principalData = [];
    var interestData = [];
    var balanceData = [];

    chartSchedule.forEach(function (year) {
      cumulativePrincipal += year.principalPaid;
      cumulativeInterest += year.interestPaid;
      principalData.push(cumulativePrincipal);
      interestData.push(cumulativeInterest);
      balanceData.push(year.endingBalance);
    });

    return {
      loanAmount: loanAmount,
      downPaymentPercent: downPaymentPercent,
      exactDownPaymentRatio: exactDownPaymentRatio,
      monthlyInterestRate: monthlyInterestRate,
      numberOfPayments: numberOfPayments,
      monthlyPI: monthlyPI,
      monthlyTaxInsurance: monthlyTaxInsurance,
      totalMonthlyPayment: monthlyPI + monthlyTaxInsurance,
      amortizationSchedule: amortizationSchedule,
      amortizationScheduleWithExtra: amortizationScheduleWithExtra,
      totalPaid: totalPaid,
      totalInterest: totalInterest,
      totalInterestWithoutExtra: totalInterestWithoutExtra,
      interestSaved: input.extraPayment > 0 ? totalInterestWithoutExtra - totalInterest : 0,
      actualLoanTerm: actualLoanTerm,
      payoffYear: startYear + actualLoanTerm,
      chart: {
        labels: chartSchedule.map(function (y) { return y.year; }),
        principalData: principalData,
        interestData: interestData,
        balanceData: balanceData
      }
    };
  }

  return {
    calculate: calculate,
    calculateMonthlyPI: calculateMonthlyPI,
    buildAmortizationSchedule: buildAmortizationSchedule
  };
});
